
import firebase from "firebase/app";
import { Options, Vue } from "vue-class-component";
import MButton from "@/components/MButton.vue";
import MIcon from "@/components/MIcon.vue";
import MLoadingIcon from "@/components/MLoadingIcon.vue";
import MTable from "@/components/MTable.vue";
import MNavBar from "@/components/MNavBar.vue";
import MHeaderPanel from "@/components/MHeaderPanel.vue";
import store from "@/store";
import { State } from "@/store/helper";
import { journalsTableDefinition } from "@/utils/table";
import { Student } from "@/entities/student";
import { School, SchoolConfig } from "@/entities/school";
import { Domains } from "@/env";
import { Journal, JournalData } from "@/entities/journal";
import {
  deleteJournal,
  getJournalAnalysisPrompt,
  getJournalsOfSchool
} from "@/api/journal";
import { TutorMeta } from "@/api/tutor";
import dayjs from "dayjs";
import JournalAddModal from "@/components/journal/JournalAddModal.vue";
import JournalEditModal from "@/components/journal/JournalEditModal.vue";
import JournalAnalysisModal from "@/components/journal/JournalAnalysisModal.vue";

export type JournalForTable = JournalData & {
  id: string;
  ref: firebase.firestore.DocumentReference;
  publishedTimeStamp: string;
  createdTimeStamp: string;
  targetStudents: string;
  status: "完了" | "未完了";
  draftStatus: "未公開" | "公開中";
};

@Options({
  components: {
    MButton,
    MIcon,
    MLoadingIcon,
    MTable,
    MNavBar,
    MHeaderPanel,
    JournalAddModal,
    JournalEditModal,
    JournalAnalysisModal
  }
})
export default class JournalList extends Vue {
  @State("students") students?: Student[];
  @State("school") school?: School;
  @State("schoolConfig") schoolConfig!: SchoolConfig | null;
  @State("tutor") tutor?: TutorMeta;

  prompt: string | null = "";

  load = false;
  searchKeyword = "";
  selectedId = "";
  selectedIds: string[] = [];
  studentsDisplayLimit = 5000;
  definitions = journalsTableDefinition;
  journals: JournalForTable[] = [];
  timerId = -1;
  displayAddModal = false;
  displayEditModal = false;
  displayAiModal = false;
  aiTargetJournal: JournalForTable | null = null;

  get isStudyRoomOnly(): boolean {
    return this.schoolConfig?.data.useStudyRoomOnly ?? false;
  }

  get newJournalManagementUrl(): string {
    const domains = new Domains({ usePrefix: true });
    return `${domains.domains.schoolAi}/journal_management`;
  }

  get selectedJournals(): JournalForTable[] | null {
    const matchJournals = this.journals.filter(item =>
      this.selectedIds.includes(item.id)
    );
    if (matchJournals.length === 0) {
      return null;
    }
    return matchJournals;
  }

  get filteredJournals(): JournalForTable[] {
    if (!this.searchKeyword) return this.journals;

    return this.journals.filter(journal =>
      journal.title.includes(this.searchKeyword)
    );
  }

  get studentsOfSchool(): Student[] {
    if (!this.school) return [];
    if (!this.students) return [];

    return this.students.filter(student =>
      student.ref.path.split("/").includes(this.school!.ref.id)
    );
  }

  get selectedJournal(): JournalForTable | null {
    const matchJournal = this.journals.find(
      item => item.id === this.selectedId
    );
    return matchJournal || null;
  }

  handleAiReflectionClick({
    id,
    buttonKey
  }: {
    id: string;
    buttonKey: string;
  }) {
    if (buttonKey !== "ai") {
      return;
    }

    const target = this.journals.find(item => item.id === id);
    if (!target) return;

    this.aiTargetJournal = target;
    this.displayAiModal = true;
  }

  showAddModal() {
    this.displayAddModal = true;
  }

  showEditModal(id: string) {
    this.selectedId = id;
    this.displayEditModal = true;
  }

  changeSelectedId(id: string) {
    if (this.selectedIds.includes(id)) {
      this.selectedIds = this.selectedIds.filter(item => item !== id);
    } else {
      this.selectedIds.push(id);
    }
  }

  syncAddition(data: Journal) {
    const journal = this.convertJournalForTable(data);

    journal.targetStudents = journal.availableToAllStudents
      ? "全員"
      : journal.studentIds
          .map(id => {
            const _student = this.studentsOfSchool.find(
              student => student.ref.id === id
            );
            return _student ? _student.data.name : "";
          })
          .filter(_ => _)
          .join(", ");

    this.journals.unshift(journal);
  }

  syncUpdate(updatedJournal: JournalForTable) {
    const index = this.journals.findIndex(
      journal => journal.id === updatedJournal.id
    );
    if (index !== -1) {
      this.journals[index] = updatedJournal;
    }
  }

  syncDelete(deletedIds: string[]) {
    this.journals = this.journals.filter(
      journal => !deletedIds.includes(journal.id)
    );
  }

  async deleteSelectedJournals() {
    if (this.load) return;
    if (!this.tutor) return;
    if (!this.selectedJournals) return;

    if (!window.confirm("選択したジャーナルを削除しますか？")) {
      return;
    }

    try {
      store.commit("START_LOADING", "削除中...");
      await Promise.all(
        this.selectedJournals.map(({ ref }) =>
          deleteJournal(ref, this.tutor!.main.ref.id)
        )
      );

      this.syncDelete(this.selectedJournals.map(journal => journal.id));
      this.selectedIds = [];
      alert("削除しました。");
    } catch (e) {
      console.error(e);
      alert(
        `${this.selectedJournals.length > 1 &&
          "一部"}ジャーナルの削除に失敗しました。` +
          "\n" +
          (e instanceof Error ? e.message : `unknown error`)
      );
    } finally {
      store.commit("END_LOADING");
    }
  }

  prevStudentsLength = 0;
  updateTargetStudents() {
    if (this.studentsOfSchool.length === 0) return;
    if (this.prevStudentsLength === this.studentsOfSchool.length) {
      return;
    }

    this.prevStudentsLength = this.studentsOfSchool.length;

    this.journals = this.journals.map(journal => {
      const text = journal.availableToAllStudents
        ? "全員"
        : journal.studentIds
            .map(id => {
              const found = this.studentsOfSchool.find(
                student => student.ref.id === id
              );
              return found ? found.data.name : "";
            })
            .filter(_ => _)
            .join(", ");

      return { ...journal, targetStudents: text };
    });

    if (store.state.allStudentsFetched) {
      this.clearTimerId();
    }
  }

  clearTimerId() {
    if (this.timerId > -1) {
      window.clearInterval(this.timerId);
    }
  }

  convertJournalForTable(journal: Journal): JournalForTable {
    const {
      createdAt,
      publishedAt,
      studentIds,
      isCompleted,
      isDraft
    } = journal.data;

    const createdTimeStamp = dayjs.unix(createdAt).format("YYYY-MM-DD");
    const publishedTimeStamp = publishedAt
      ? dayjs.unix(publishedAt).format("YYYY-MM-DD")
      : "-";
    const targetStudents: string =
      studentIds.length > this.studentsDisplayLimit
        ? `${this.studentsDisplayLimit}件を超えており表示できません`
        : `計算中...`;
    const status = isCompleted ? "完了" : "未完了";
    const draftStatus = isDraft ? "未公開" : "公開中";

    return {
      ...journal.data,
      ref: journal.ref,
      id: journal.ref.id,
      targetStudents,
      status,
      createdTimeStamp,
      publishedTimeStamp,
      draftStatus
    };
  }

  async created(): Promise<void> {
    this.load = true;
    try {
      if (!this.school) return;
      if (!this.tutor) return;

      const [journals, prompt] = await Promise.all([
        getJournalsOfSchool(this.school.ref, this.tutor.config),
        getJournalAnalysisPrompt()
      ]);

      this.journals = journals
        .map(journal => this.convertJournalForTable(journal))
        .sort((a, b) => b.createdAt - a.createdAt);

      this.prompt = prompt && prompt.data.active ? prompt.data.prompt : null;

      this.clearTimerId();
      this.timerId = window.setInterval(() => {
        this.updateTargetStudents();
      }, 500);
    } catch (error) {
      alert(
        "成長ジャーナルの取得に失敗しました" +
          "\n" +
          (error instanceof Error ? error.message : "unknown error")
      );
      console.error("Error when getting journals", error);
    } finally {
      this.load = false;
    }
  }

  beforeUnmount(): void {
    this.clearTimerId();
  }
}
